import React from "react";
import { useTranslation } from "react-i18next";
import "../../styles/filters.css";

interface TableHeaderFilterProps {
  column: string;
  title: string;
  data: any[];
  filterPanels: { [key: string]: boolean };
  filters: { [key: string]: string[] };
  searchQueries: { [key: string]: string };
  panelRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>;
  toggleFilterPanel: (column: string) => void;
  handleFilterChange: (column: string, value: string) => void;
  handleFilterSearch: (column: string, query: string) => void;
  setFilters: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  getLocalizedValue?: (value: any) => string;
  lastColumn?: boolean;
}

const TableHeaderFilter: React.FC<TableHeaderFilterProps> = ({
  column,
  title,
  data,
  filterPanels,
  filters,
  searchQueries,
  panelRefs,
  toggleFilterPanel,
  handleFilterChange,
  handleFilterSearch,
  setFilters,
  getLocalizedValue,
  lastColumn = false,
}) => {
  const { t } = useTranslation();

  const query = searchQueries[column]?.toLowerCase() || "";

  // Check if there are null/empty values in the data for this column
  const hasEmptyValues = data.some((record) => {
    const val = record[column];
    return val == null || val === "";
  });

  // Get unique values
  const originalUniqueValues = Array.from(
    new Set(data.map((record) => record[column]))
  ).filter((value) => value != null && value !== "");

  // Function to get display string
  const getDisplayValue = (val: any) => {
    if (getLocalizedValue) return getLocalizedValue(val);
    // If it's a translation key (no spaces usually), translate it. 
    // Otherwise rely on t() handling it or just stringify.
    return t(val?.toString() || "");
  };

  // Filter values based on search query
  let filteredOriginalValues = originalUniqueValues.filter((originalValue) => {
    const displayValue = getDisplayValue(originalValue);
    return displayValue.toString().toLowerCase().includes(query);
  });

  // Sort values
  filteredOriginalValues.sort((a, b) => {
    const displayA = getDisplayValue(a);
    const displayB = getDisplayValue(b);
    return displayA.localeCompare(displayB);
  });

  // "Select All" Logic
  const allVisibleChecked =
    filteredOriginalValues.length > 0 &&
    filteredOriginalValues.every((val) => filters[column]?.includes(val.toString())) &&
    (!hasEmptyValues || (filters[column]?.includes("") ?? false));

  const handleSelectAll = (checked: boolean) => {
    const valuesToModify = filteredOriginalValues.map(v => v.toString());
    if (hasEmptyValues) {
      valuesToModify.push("");
    }

    setFilters((prevState) => {
      const currentFilters = prevState[column] || [];
      let newFilters;

      if (checked) {
        newFilters = Array.from(new Set([...currentFilters, ...valuesToModify]));
      } else {
        newFilters = currentFilters.filter((v) => !valuesToModify.includes(v));
      }

      return {
        ...prevState,
        [column]: newFilters,
      };
    });
  };

  const totalPossibleValuesCount = originalUniqueValues.length + (hasEmptyValues ? 1 : 0);
  const currentSelectedCount = filters[column]?.length ?? 0;
  const isFilterActive =
    filters[column] &&
    currentSelectedCount > 0 &&
    currentSelectedCount < totalPossibleValuesCount;

  return (
    <>
      <span
        onClick={() => toggleFilterPanel(column)}
        style={{ cursor: "pointer" }}
      >
        {title}
        <span style={{ fontSize: "1.3em", fontWeight: "bold", color: isFilterActive ? "rgb(166, 57, 57)" : "inherit" }}> &#9013;</span>
      </span>
      {filterPanels[column] && (
        <div
          ref={(el) => (panelRefs.current[column] = el)}
          className={lastColumn ? "filter-last-panel" : "filter-panel"}
          style={{
            maxHeight: "calc(100vh / 2)",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#EFF0F2 transparent",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <input
              type="text"
              placeholder={t("search")}
              style={{
                width: "94%",
                backgroundColor: "#eff0f2",
                border: "none",
                outline: "none",
                borderRadius: "10px",
                color: "#404040",
                height: "2.7vh",
                maxHeight: "30px",
                fontSize: "1em",
                paddingLeft: "10px",
                marginTop: "10px",
              }}
              value={query}
              onChange={(e) => handleFilterSearch(column, e.target.value)}
            />
          </div>
          {query === "" && (
            <ul
              style={{
                listStyleType: "none",
                padding: 0,
                marginBottom: "1vh",
                marginTop: "1vh",
              }}
            >
              <li className="filters-form-check filters-checkbox">
                <label
                  className="filters-checkbox-label"
                  style={{ display: "inline-block" }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleChecked}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    style={{ display: "none" }}
                  />
                  <span
                    className="filters-checkmark"
                    style={{ backgroundColor: "white" }}
                  ></span>
                  <span className="form-check-label">{t("Select_all")}</span>
                </label>
              </li>
              {hasEmptyValues && (
                <li className="filters-form-check filters-checkbox">
                  <label
                    className="filters-checkbox-label"
                    style={{ display: "inline-block" }}
                  >
                    <input
                      type="checkbox"
                      checked={filters[column]?.includes("") || false}
                      onChange={() => handleFilterChange(column, "")}
                    />
                    <span
                      className="filters-checkmark"
                      style={{ backgroundColor: "white" }}
                    ></span>
                    <span className="form-check-label">{t("empty")}</span>
                  </label>
                </li>
              )}
            </ul>
          )}
          <ul
            style={{
              listStyleType: "none",
              padding: 0,
              marginBottom: "1vh",
              marginTop: "1vh",
            }}
          >
            {filteredOriginalValues.map((originalValue) => (
              <li
                key={originalValue.toString()}
                className="filters-form-check filters-checkbox"
              >
                <label
                  className="filters-checkbox-label"
                  style={{ display: "inline-block" }}
                >
                  <input
                    type="checkbox"
                    checked={
                      filters[column]?.includes(originalValue.toString()) ||
                      false
                    }
                    onChange={() =>
                      handleFilterChange(column, originalValue.toString())
                    }
                  />
                  <span
                    className="filters-checkmark"
                    style={{ backgroundColor: "white" }}
                  ></span>
                  <span className="form-check-label">
                    {getDisplayValue(originalValue) || t("empty")}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default TableHeaderFilter;